import React from 'react';

export const LoginForm = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  showPass,
  setShowPass,
  captchaText,
  setCaptchaText,
  captchaSvg,
  fetchCaptcha,
  handleLogin
}) => {
  return (
    <div className="flex-1 p-8 sm:p-12 lg:p-16 flex flex-col justify-between bg-white relative">
      {/* Avatar Circle & WELCOME Header */}
      <div className="flex flex-col items-center mt-4 mb-6">
        {/* Verve Kitchens Luxury Brand Logo */}
        <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center mb-4 relative overflow-hidden border-2 border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.03)] p-4">
          <img
            src="/verve-kitchens-logo-01.png"
            alt="Verve Kitchens Logo"
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="text-3xl font-extrabold tracking-wider text-slate-800 uppercase">
          WELCOME
        </h2>
        <span className="text-[9px] font-bold tracking-[0.25em] text-[#b5966a] uppercase mt-1">
          Verve Workspace Portal
        </span>
      </div>

      {/* Core Login Form */}
      <div className="w-full max-w-sm mx-auto my-auto">
        <form onSubmit={handleLogin} className="space-y-6">
          
          {/* Input: Username or Email */}
          <div className="relative border-b-2 border-[#b5966a] py-2 flex items-end gap-3 transition-colors duration-300">
            {/* Person Silhouette Icon in Champagne Gold */}
            <div className="text-[#b5966a] pb-0.5">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <label className="block text-[11px] font-bold tracking-wide text-slate-400">
                Username
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-transparent border-none outline-none text-slate-700 text-sm font-semibold py-0.5 placeholder-slate-350"
                placeholder="name@verve.com"
                required
              />
            </div>
          </div>

          {/* Input: Password */}
          <div className="relative border-b-2 border-slate-200 focus-within:border-[#b5966a] py-2 flex items-end gap-3 transition-colors duration-300">
            {/* Lock Icon in Slate */}
            <div className="text-slate-300 pb-0.5 transition-colors duration-300 focus-within:text-[#b5966a]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <label className="block text-[11px] font-bold tracking-wide text-slate-400">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="text-[10px] text-slate-400 hover:text-[#b5966a] font-bold transition-colors duration-200"
                >
                  {showPass ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-transparent border-none outline-none text-slate-700 text-sm font-semibold py-0.5 placeholder-slate-350"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Unified CAPTCHA Section */}
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex justify-between items-center">
              <label className="block text-[11px] font-bold tracking-wide text-slate-400">
                Security CAPTCHA
              </label>
              <button
                type="button"
                onClick={fetchCaptcha}
                className="text-[10px] text-slate-400 hover:text-[#b5966a] font-bold transition-colors flex items-center gap-1"
                title="Refresh CAPTCHA"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                REFRESH
              </button>
            </div>
            
            <div className="flex items-end gap-3">
              {/* CAPTCHA Image */}
              <div 
                className="bg-white rounded overflow-hidden h-[40px] w-[120px] flex-shrink-0 flex items-center justify-center border border-slate-200 shadow-sm"
                dangerouslySetInnerHTML={{ __html: captchaSvg }}
              />
              {/* CAPTCHA Input */}
              <div className="relative border-b-2 border-slate-200 focus-within:border-[#b5966a] flex-1 pb-1 transition-colors duration-300">
                <input
                  type="text"
                  value={captchaText}
                  onChange={e => setCaptchaText(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-slate-700 text-sm font-semibold placeholder-slate-350"
                  placeholder="Enter characters"
                  required
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-3.5 text-sm font-bold tracking-widest uppercase shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Signing in...
              </>
            ) : 'LOGIN'}
          </button>
        </form>
      </div>
    </div>
  );
};
